/**
 * TODO: 
 * - add onloadscreen
 */

import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import Skybox from './Skybox';
import pathToModel1 from '../../assets/models/floating_island.glb';
import pathToModel2 from '../../assets/models/baby.glb';
import pathToTex1 from '../../assets/img/textures/glow.png';
import pathToTex2 from '../../assets/img/textures/bump.jpg';
import Particles from "./Particles";
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.clock = new THREE.Clock();
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/eeeeff-7f7caf-9fb4c7-28587b-9fb798');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.isDisposed = false;
        this.loader = new THREE.TextureLoader();

        this.setupCamera();
        this.setupLight();
        this.setupControls();
        this.createSky();
        this.loadModels();
        this.setupGeometry();
        this.setupParticles();
    }

    setupCamera() {
        let VIEW_ANGLE = 50;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 1;
        let FAR_PLANE = 1000;
        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(-13, 8, -94);
        this.camera.position.set(-59, 11, 79);
    }

    setupLight() {
        const hemiLight = new THREE.HemisphereLight(this.colourArr[0], this.colourArr[1], 2);
        hemiLight.position.set(0, 50, 0);
        this.scene.add(hemiLight);

        let pointLight = new THREE.PointLight(this.colourArr[3], 1.8);
        pointLight.position.set(2.5, 10, 2.5);
        this.scene.add(pointLight);

        let pointLight2 = new THREE.PointLight(this.colourArr[3], 1.8);
        pointLight2.position.set(-2.5, 0, -2.5);
        this.scene.add(pointLight2);

        let pointLight3 = new THREE.PointLight(this.colourArr[3], 0.8);
        pointLight3.position.set(0.5, -0.2, 0);
        this.scene.add(pointLight3);
    }

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.autoRotate = true;
        this.controls.autoRotateSpeed = 0.5;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.3;
        this.controls.maxDistance = 200;
        this.controls.minDistance = 100;
    }

    createSky() {
        let sky = new Skybox(this.scene, this.colourArr[1], this.colourArr[4]);
        sky.createSky();
    }

    setupGlowMaterial(mesh) {   //from https://stemkoski.github.io/Three.js/Simple-Glow.html
        let spriteTexture = this.loader.load(pathToTex1);
        let spriteMaterial = new THREE.SpriteMaterial(
            {
                map: spriteTexture,
                color: this.colourArr[4],
                transparent: false,
                blending: THREE.SubtractiveBlending
            });
        let sprite = new THREE.Sprite(spriteMaterial);
        sprite.scale.set(20, 20, 20);
        sprite.position.set(-2, 0, 0);
        mesh.add(sprite); // this centers the glow at the mesh
    }

    setupMaterialOne() {
        let map = this.loader.load(pathToTex2);
        let material = new THREE.MeshPhongMaterial({
            color: this.colourArr[1], //0x00FFFF // 0x5b8a7b
            specular: 0x636945,
            shininess: 20,
            bumpMap: map,
            bumpScale: 3
        });
        return material;
    }

    setupMaterialTwo() {
        let material = new THREE.MeshPhongMaterial({
            color: this.colourArr[0],
            specular: 0x222222,
            shininess: 50,
            wireframe: true
        });
        return material;
    }

    loadModels() {
        let material1 = this.setupMaterialOne();
        let group = new THREE.Group();
        this.scene.add(group);
        group.position.set(0, -6, 0);

        let model1 = null;
        let model2 = null;
        const gltfLoader = new GLTFLoader();
        gltfLoader.load(pathToModel1, (gltf) => {
            model1 = gltf.scene;
            model1.scale.multiplyScalar(2);
            // this.setupGlowMaterial(model1);
            group.add(model1);

            model1.traverse((node) => {
                if (node.isMesh) {
                    node.material = material1;
                    node.castShadow = true;
                    node.receiveShadow = true;
                }
            });
        });

        gltfLoader.load(pathToModel2, (gltf) => {
            model2 = gltf.scene;
            model2.position.set(-5, 20, 0);
            model2.rotateZ(Math.PI * 0.5);
            model2.rotateY(Math.PI * 0.5);
            group.add(model2);
        });
    }

    scatterRandomGeometry() {
        this.meshElems = new THREE.Object3D();
        this.meshElems.position.set(-60, 10, 0);
        this.scene.add(this.meshElems);

        let elemsNum = 20;
        let geo = new THREE.IcosahedronBufferGeometry(1, 0);
        let mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
            color: this.colourArr[1],
            map: this.groundMap,
            transparent: true,
            opacity: 0.5
        }));
        this.meshElems.add(mesh);

        //spiral 
        let stepAngle = Math.PI * 2 / elemsNum;
        let xStep = 0.5;
        let turns = 12;
        for (let i = 0; i < turns * elemsNum; i++) {
            let elem = mesh.clone();
            let angle = stepAngle * i;
            let dist = 30 + Math.random() * 30; //the distance to the center of the axis
            let s = 1 + Math.random() * 2;

            elem.position.y = Math.sin(angle) * dist;
            elem.position.x = xStep * i; //Math.cos(angle)*dist;
            elem.position.z = Math.cos(angle) * dist;

            elem.rotation.z = angle + Math.PI / 2;
            elem.scale.set(s, s, s);
            this.meshElems.add(elem);
        }
    }

    scatterRandWireframeGeo() {
        this.meshElems = new THREE.Object3D();
        this.scene.add(this.meshElems);

        let elemsNum = 30;
        let geo = new THREE.IcosahedronBufferGeometry(0.5, 0);
        let material = this.setupMaterialTwo()
        let mesh = new THREE.Mesh(geo, material);
        mesh.position.set(-3, 20, 0);
        mesh.receiveShadow = true;
        this.meshElems.add(mesh);

        let stepAngle = Math.PI * 2 / elemsNum;
        for (let i = 0; i < elemsNum; i++) {
            let elem = mesh.clone();
            let angle = stepAngle * i;
            let dist = 30 + Math.random() * 30; //the distance to the center of the axis
            let s = 1 + Math.random() * 2;

            elem.position.y = Math.sin(angle) * dist;
            elem.position.x = Math.cos(angle) * dist;
            elem.position.z = Math.cos(angle) * dist;
            elem.rotation.z = angle + Math.PI / 2;
            elem.scale.set(s, s, s);
            this.meshElems.add(elem);
        }
    }

    setupGeometry() {
        this.scatterRandWireframeGeo();
        this.scatterRandomGeometry();
    }

    setupParticles() {
        this.particles = new Particles(this.scene, this.colourArr[2], 1000);
    }

    updateSceneSubjects(time) {
        for (let i = 0; i < this.meshElems.children.length; i++) {
            let elem = this.meshElems.children[i];
            elem.rotation.z += Math.random() * .005; //*(i+1);
            elem.rotation.y += Math.random() * .02; //*(i+1);
        };
        this.particles.update(Date.now() * 0.005);
    }

    update() {
        if (this.isDisposed) return;
        let delta = this.clock.getDelta();
        if (isNaN(delta) || delta > 1000 || delta === 0) {
            delta = 1000 / 60;
        }
        if (this.controls) this.controls.update();
        this.updateSceneSubjects(delta);
        this.renderer.render(this.scene, this.camera);
        // console.log(this.camera.position);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    dispose() {
        this.isDisposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                child.material.dispose();
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
    }
}


